import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CircleSlash, FilterIcon, ArrowLeft, AlertTriangle, Edit2, Check, X, Ban } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2 } from "lucide-react";
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import { useToast } from '@/components/ui/use-toast';
import { dryingCyclesColumns, columnsExcel } from './utils/dryingCyclesColumns';
import { fetchDryingCycles, createDryingCycle, updateDryingCycle, deleteDryingCycle } from './services/dryingCycles.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';
import AppliedFilters from '@/components/AppliedFilters';
import DynamicFormModal from '@/components/DynamicFormModal';
import EditDryingCycleModal from './components/EditDryingCycleModal';
import { useAuth } from '@/contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchDryers, updateDryerCounter } from '@/pages/parametrizacion/dryers/Services/dryers.services';

const ITEMS_PER_PAGE = 10;

const ABORT_REASONS = [
  'Mantenimiento',
  'Problemas eléctricos',
  'Error Máquina',
  'Programa mal seleccionado',
  'Otro',
];

const ABORTED_FORM_INIT = { machineId: '', ciclos: '', razon: '', otraRazon: '', fecha: '' };

const DryingCyclesPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();
  const { isAdmin } = useAuth();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [laundryName, setLaundryName] = useState('');

  const today = new Date();
  const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 0, 0, 0).toISOString();
  const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();

  const [filters, setFilters] = useState({
    lavanderia: { id: idLavanderia },
    fecha: { $gte: startOfDay, $lte: endOfDay },
  });

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [machines, setMachines] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [columns, setColumns] = useState([]);
  const [excelColumns] = useState(columnsExcel);
  const [machineCounters, setMachineCounters] = useState({});
  const [newCounterValues, setNewCounterValues] = useState({});
  const [editingCounter, setEditingCounter] = useState({});
  const [tempCounterValues, setTempCounterValues] = useState({});
  const [selectedDates, setSelectedDates] = useState({});
  const [abortedModalOpen, setAbortedModalOpen] = useState(false);
  const [abortedForm, setAbortedForm] = useState(ABORTED_FORM_INIT);

  const buildQuery = (filters) => {
    const query = { ...filters };
    if (query.maquina && typeof query.maquina === 'string') {
      query['maquina.id'] = query.maquina;
      delete query.maquina;
    }
    return query;
  };

  const fetchAndSetDryingCycles = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = buildQuery(filters);
      const response = await fetchDryingCycles({ limit: ITEMS_PER_PAGE, page: currentPage, query });
      const data = response?.data || [];
      const totalRecords = response?.totalRecords || 0;
      setDisplayedData(Array.isArray(data) ? data : []);
      setTotalItems(totalRecords);
    } catch (error) {
      setDisplayedData([]);
      toast({
        title: 'Error',
        description: error.message || 'No se pudieron obtener los ciclos de secado.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    const initialize = async () => {
      setLoadingMachines(true);
      try {
        const response = await fetchDryers({
          limit: 100,
          offset: 1,
          query: { "lavanderia.id": idLavanderia },
        });

        if (response && response.data) {
          const fetchedMachines = response.data;
          setMachines(fetchedMachines);

          const countersData = {};
          const newCounters = {};
          fetchedMachines.forEach(machine => {
            countersData[machine._id] = machine.contador || 0;
            newCounters[machine._id] = machine.contador || 0;
          });

          if (fetchedMachines.length > 0) {
            setLaundryName(fetchedMachines[0].lavanderia.nombre);
          }

          setMachineCounters(countersData);
          setNewCounterValues(newCounters);

          const machineOptions = fetchedMachines.map(machine => ({
            label: machine.nombre,
            value: machine._id,
          }));

          const updatedColumns = [...dryingCyclesColumns].map(column => {
            if (column.id === 'maquina') {
              return {
                ...column,
                options: machineOptions,
                onChange: (selected, formData) => {
                  if (selected && formData) {
                    const capacidad = selected.value.capacidad || 0;
                    const ciclos = formData.ciclos || 0;
                    return { ...formData, capacidad, kilosSecados: capacidad * ciclos };
                  }
                  return formData;
                },
              };
            }
            if (column.id === 'ciclos') {
              return {
                ...column,
                onChange: (value, formData) => {
                  if (formData && formData.capacidad) {
                    const ciclos = value || 0;
                    const capacidad = formData.capacidad || 0;
                    return { ...formData, kilosSecados: capacidad * ciclos };
                  }
                  return formData;
                },
              };
            }
            return column;
          });

          setColumns(updatedColumns);
        }
      } catch (error) {
        toast({
          title: 'Error',
          description: error.message || 'No se pudieron cargar las máquinas.',
          variant: 'destructive',
        });
      } finally {
        setLoadingMachines(false);
      }
    };

    initialize();
  }, [idLavanderia, toast]);

  useEffect(() => {
    fetchAndSetDryingCycles();
  }, [fetchAndSetDryingCycles]);

  const handleSaveItem = async (itemData) => {
    try {
      setIsLoading(true);

      let maquinaObj = null;
      const mv = itemData.maquina;
      if (mv) {
        if (typeof mv === 'object') {
          const id = mv.id || mv._id;
          const nombre = mv.nombre || machines.find(m => m._id === id || m.id === id)?.nombre || '';
          maquinaObj = id ? { id, nombre } : null;
        } else {
          const mach = machines.find(m => m._id === mv || m.id === mv);
          maquinaObj = { id: mv, nombre: mach?.nombre || '' };
        }
      }

      if (maquinaObj) {
        const machine = machines.find(m => m._id === maquinaObj.id || m.id === maquinaObj.id);
        const ciclos = itemData.ciclos || 0;
        if (machine && machine.consumoLimiteDia && ciclos > machine.consumoLimiteDia) {
          toast({
            title: "Error - Consumo excesivo",
            description: `Los ciclos ingresados (${ciclos}) superan el límite diario configurado (${machine.consumoLimiteDia}) para ${machine.nombre}. Por favor verifique el contador ingresado.`,
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      }

      const machineId = maquinaObj?.id;
      const dateToUse = (machineId && selectedDates[machineId]) || itemData.fecha || getDefaultDate();
      const [year, month, day] = dateToUse.split('-');
      const recordDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));

      const formattedData = {
        ...itemData,
        maquina: maquinaObj || null,
        fecha: recordDate.toISOString(),
        lavanderia: itemData.lavanderia || { id: idLavanderia, nombre: laundryName },
      };

      const isCardSave = !modalMode || modalMode === '' || (modalMode === 'create' && !currentItem);

      if (isCardSave) {
        await createDryingCycle(formattedData);
        toast({ title: 'Éxito', description: 'Ciclos de secado registrados correctamente' });

        if (itemData.maquina && itemData.maquina.id && newCounterValues[itemData.maquina.id]) {
          setMachineCounters(prev => ({ ...prev, [itemData.maquina.id]: newCounterValues[itemData.maquina.id] }));
          setMachines(prev => prev.map(machine => {
            if (machine._id === itemData.maquina.id) {
              return { ...machine, contador: newCounterValues[itemData.maquina.id] };
            }
            return machine;
          }));
        }
      } else if (modalMode === 'create') {
        await createDryingCycle(formattedData);
        toast({ title: 'Éxito', description: 'Ciclos de secado registrados correctamente' });
      } else {
        await updateDryingCycle(currentItem._id, formattedData);
        toast({ title: 'Éxito', description: 'Ciclos de secado actualizados correctamente' });
      }

      setIsModalOpen(false);
      setCurrentItem(null);
      setModalMode('');
      fetchAndSetDryingCycles();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Error al guardar los datos.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleOpenModal = (mode, item = null) => {
    if (mode === 'edit' && !isAdmin()) {
      toast({
        title: "Acceso restringido",
        description: "Solo los administradores pueden editar registros.",
        variant: "destructive",
      });
      return;
    }
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleDeleteConfirmation = (item) => {
    if (!isAdmin()) {
      toast({
        title: "Acceso restringido",
        description: "Solo los administradores pueden eliminar registros.",
        variant: "destructive",
      });
      return;
    }
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    try {
      if (itemToDelete) {
        const response = await deleteDryingCycle(itemToDelete._id);
        if (response.code === 200) {
          toast({ title: "Ciclo eliminado", description: "El registro fue eliminado correctamente." });
          fetchAndSetDryingCycles();
        } else {
          toast({ title: "Error", description: "No se pudo eliminar el registro.", variant: "destructive" });
        }
      }
    } catch (error) {
      console.error("Error eliminando ciclo de secado:", error);
      toast({
        title: "Error",
        description: error.message || "Ocurrió un problema al eliminar el registro.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleAction = (actionType, row) => {
    switch (actionType) {
      case 'edit': handleOpenModal('edit', row); break;
      case 'delete': handleDeleteConfirmation(row); break;
      default: break;
    }
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters({ ...newFilters, idLavanderia });
  };

  const handleRemoveFilter = (key) => {
    setCurrentPage(1);
    const newFilters = { ...filters };
    if (key !== 'idLavanderia') {
      delete newFilters[key];
      setFilters(newFilters);
    }
  };

  const handleCounterChange = (machineId, value) => {
    const numValue = parseFloat(value) || 0;
    setNewCounterValues(prev => ({ ...prev, [machineId]: numValue }));
  };

  const calculateCycles = (machineId) => {
    const currentValue = machineCounters[machineId] || 0;
    const newValue = newCounterValues[machineId] || 0;
    return Math.max(0, newValue - currentValue);
  };

  const getDefaultDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate());
    return yesterday.toISOString().split('T')[0];
  };

  /**
   * Obtiene la fecha de HOY en zona horaria local en formato YYYY-MM-DD.
   * Se usa SOLO para registrar ciclos abortados, ya que el operador los
   * registra el mismo día en que ocurren, de forma independiente al
   * contador normal de la secadora (que se registra al día siguiente).
   * Devuelve la fecha local (no UTC) para evitar el desfase de un día
   * que se produce al usar `toISOString()` cerca del cambio de jornada.
   */
  const getAbortedCycleDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (machineId, value) => {
    setSelectedDates(prev => ({ ...prev, [machineId]: value }));
  };

  const handleStartEditCounter = (machineId, currentCounter) => {
    setEditingCounter(prev => ({ ...prev, [machineId]: true }));
    setTempCounterValues(prev => ({ ...prev, [machineId]: currentCounter }));
  };

  const handleCancelEditCounter = (machineId) => {
    setEditingCounter(prev => ({ ...prev, [machineId]: false }));
    setTempCounterValues(prev => ({ ...prev, [machineId]: '' }));
  };

  const handleAbortedFormChange = (field, value) => {
    setAbortedForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveAbortedCycle = async () => {
    const machine = machines.find(m => m._id === abortedForm.machineId);
    if (!machine) {
      toast({ title: 'Error', description: 'Selecciona una secadora.', variant: 'destructive' });
      return;
    }
    const razonFinal = abortedForm.razon === 'Otro' ? abortedForm.otraRazon?.trim() : abortedForm.razon;
    if (!abortedForm.ciclos || parseInt(abortedForm.ciclos) <= 0) {
      toast({ title: 'Error', description: 'Ingresa la cantidad de ciclos abortados.', variant: 'destructive' });
      return;
    }
    if (!razonFinal) {
      toast({ title: 'Error', description: 'Selecciona o especifica la razón del abortado.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const dateToUse = abortedForm.fecha || getAbortedCycleDate();
      const [year, month, day] = dateToUse.split('-');
      const recordDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0));
      await createDryingCycle({
        maquina: { id: machine._id, nombre: machine.nombre },
        ciclos: parseInt(abortedForm.ciclos),
        capacidad: machine.capacidad || 0,
        kilosSecados: 0,
        lavanderia: { id: idLavanderia, nombre: laundryName },
        abortado: true,
        razonAbortado: razonFinal,
        contador: machine.contador || 0,
        contadorNuevo: machine.contador || 0,
        fecha: recordDate.toISOString(),
      });
      toast({ title: 'Éxito', description: 'Ciclos abortados registrados correctamente.' });
      setAbortedForm(ABORTED_FORM_INIT);
      setAbortedModalOpen(false);
      fetchAndSetDryingCycles();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Error al registrar ciclos abortados.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCounter = async (machine) => {
    const newCounter = parseFloat(tempCounterValues[machine._id]);
    if (isNaN(newCounter) || newCounter < 0) {
      toast({
        title: "Error",
        description: "El contador debe ser un número válido mayor o igual a 0",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      await updateDryerCounter(machine._id, newCounter);
      toast({ title: "Éxito", description: `Contador actualizado para ${machine.nombre}` });

      setEditingCounter(prev => ({ ...prev, [machine._id]: false }));
      setTempCounterValues(prev => ({ ...prev, [machine._id]: '' }));
      setMachineCounters(prev => ({ ...prev, [machine._id]: newCounter }));
      setMachines(prev => prev.map(m => m._id === machine._id ? { ...m, contador: newCounter } : m));
      setNewCounterValues(prev => ({ ...prev, [machine._id]: newCounter }));
    } catch (error) {
      console.error('Error al actualizar contador:', error);
      toast({ variant: 'destructive', title: 'Error', description: 'No se pudo actualizar el contador.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="p-4 md:p-6 lg:p-8"
    >
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      <div className="mb-6">
        <div className="border border-border rounded-lg p-4 bg-card">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold tracking-tight">Registro de Ciclos de Secado</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAbortedModalOpen(true)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Ban className="mr-2 h-4 w-4" />
              Registrar Ciclos Abortados
            </Button>
          </div>
          <p className="text-muted-foreground">
            Actualiza el contador de cada secadora para el registro de los ciclos de secado
          </p>
        </div>
      </div>

      <div className="mb-6">
        {loadingMachines ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-sm text-muted-foreground">Cargando máquinas...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {machines.map(machine => (
              <Card key={machine._id} className="overflow-hidden card-gradient-bg border shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex justify-between">
                    {machine.nombre}
                    <Badge variant="outline" className="ml-2">{machine.marca}</Badge>
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Capacidad: {machine.capacidad || 0} kg
                  </CardDescription>
                </CardHeader>

                <CardContent className="pb-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Contador actual</p>
                      {editingCounter[machine._id] ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={tempCounterValues[machine._id] || ''}
                            onChange={(e) => setTempCounterValues(prev => ({ ...prev, [machine._id]: e.target.value }))}
                            className="h-8 w-20"
                            min="0"
                            step="1"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                            onClick={() => handleSaveCounter(machine)}
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleCancelEditCounter(machine._id)}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <p className="font-medium">{machineCounters[machine._id] || 0}</p>
                          {isAdmin() && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleStartEditCounter(machine._id, machineCounters[machine._id] || 0)}
                              title="Editar contador"
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Nuevo contador</p>
                      <Input
                        type="number"
                        min={machineCounters[machine._id] || 0}
                        value={newCounterValues[machine._id] || ''}
                        onChange={(e) => handleCounterChange(machine._id, e.target.value)}
                        className="h-8"
                      />
                    </div>
                  </div>

                  {machine.consumoLimiteDia && (
                    <div className="flex items-center gap-2 px-2 py-1.5 mt-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
                      <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-500" />
                      <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                        Límite diario: {machine.consumoLimiteDia} ciclos
                      </span>
                    </div>
                  )}

                  <div className="mt-3">
                    <Label htmlFor={`date-${machine._id}`} className="text-xs">Fecha del registro</Label>
                    <Input
                      id={`date-${machine._id}`}
                      type="date"
                      value={selectedDates[machine._id] || getDefaultDate()}
                      onChange={(e) => handleDateChange(machine._id, e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      className="h-8 mt-1"
                    />
                  </div>

                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-1">Ciclos calculados</p>
                    {(() => {
                      const cycles = calculateCycles(machine._id);
                      const exceedsLimit = machine.consumoLimiteDia && cycles > machine.consumoLimiteDia;
                      const nearLimit = machine.consumoLimiteDia && cycles > machine.consumoLimiteDia * 0.8 && cycles <= machine.consumoLimiteDia;

                      return (
                        <div className={`flex items-center p-2 rounded-md ${
                          exceedsLimit ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800' :
                          nearLimit ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800' :
                          cycles > 0 ? 'bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800' : ''
                        }`}>
                          <div className="flex-1">
                            <p className={`font-bold text-lg ${
                              exceedsLimit ? 'text-red-700 dark:text-red-400' :
                              nearLimit ? 'text-amber-700 dark:text-amber-400' :
                              'text-blue-700 dark:text-blue-400'
                            }`}>
                              {cycles}
                            </p>
                            {exceedsLimit && (
                              <p className="text-xs text-red-600 dark:text-red-400 font-medium mt-1">
                                ⚠ Supera el límite
                              </p>
                            )}
                          </div>
                          {cycles > 0 && (
                            <Badge className={`ml-2 ${
                              exceedsLimit ? 'bg-red-100 text-red-800 hover:bg-red-100' :
                              nearLimit ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
                              'bg-green-100 text-green-800 hover:bg-green-100'
                            }`}>
                              {(cycles * machine.capacidad).toFixed(0)} kg
                            </Badge>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>

                <CardFooter className="pt-2">
                  <Button
                    size="sm"
                    className="w-full"
                    disabled={calculateCycles(machine._id) <= 0}
                    onClick={() => handleSaveItem({
                      maquina: { id: machine._id, nombre: machine.nombre },
                      ciclos: calculateCycles(machine._id),
                      capacidad: machine.capacidad || 0,
                      kilosSecados: (calculateCycles(machine._id) * machine.capacidad).toFixed(0),
                      lavanderia: { id: idLavanderia, nombre: laundryName },
                      contador: machine.contador || 0,
                      contadorNuevo: machine.contador + calculateCycles(machine._id),
                    })}
                  >
                    Guardar ciclos
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Card className="card-gradient-bg border shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-semibold flex items-center">
            <CircleSlash className="mr-2 h-5 w-5 text-primary" />
            Ciclos de Secado
          </CardTitle>
          <CardDescription>
            Registra diariamente los ciclos de secado completados por cada máquina
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="mb-4 flex flex-wrap gap-2 justify-between items-center">
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsDrawerOpen(true)}>
                <FilterIcon className="mr-2 h-4 w-4" /> Filtros
              </Button>
              <ExportExcelButton
                service={async ({ query }) => {
                  const res = await fetchDryingCycles({ limit: 10000, page: 1, query });
                  return res?.data || [];
                }}
                filters={buildQuery(filters)}
                columns={excelColumns}
                fileName="ciclos-secado"
              />
            </div>
            <div>
              <AppliedFilters
                filters={filters}
                fields={columns.filter(c => c.isFilter)}
                onRemoveFilter={handleRemoveFilter}
                ignoreKeys={['idLavanderia']}
              />
            </div>
          </div>

          <DataTable
            data={displayedData.map(item => ({
              ...item,
              maquina: typeof item.maquina === 'object' ? item.maquina : { nombre: 'Sin máquina' },
              lavanderia: typeof item.lavanderia === 'object' ? item.lavanderia : { nombre: 'Sin lavandería' },
            }))}
            columns={columns}
            isLoading={isLoading || loadingMachines}
            onAction={handleAction}
            showCreateButton={false}
          />

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        </CardContent>
      </Card>

      <AnimatePresence>
        {isModalOpen && modalMode === 'edit' && (
          <EditDryingCycleModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            machines={machines}
            isLoading={isLoading}
          />
        )}
        {isModalOpen && modalMode === 'create' && (
          <DynamicFormModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            onSave={handleSaveItem}
            item={currentItem}
            fields={columns.filter(c => c.isForm !== false)}
            title="Registrar Ciclos de Secado"
            mode={modalMode}
          />
        )}
      </AnimatePresence>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={columns.filter(c => c.isFilter)}
        initialFilters={filters}
        onChange={handleFilterChange}
        onApply={handleFilterChange}
        initialDay={true}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro de eliminar este registro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Esto eliminará permanentemente el registro de ciclos
              de la secadora {itemToDelete?.maquina?.nombre || ''}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-destructive hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal ciclos abortados */}
      <Dialog open={abortedModalOpen} onOpenChange={(open) => { setAbortedModalOpen(open); if (!open) setAbortedForm(ABORTED_FORM_INIT); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Registrar Ciclos Abortados
            </DialogTitle>
            <DialogDescription>
              Registra ciclos de secado que no se completaron correctamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div>
              <Label className="text-sm">Secadora</Label>
              <Select value={abortedForm.machineId} onValueChange={(v) => handleAbortedFormChange('machineId', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar secadora..." />
                </SelectTrigger>
                <SelectContent>
                  {machines.map(m => (
                    <SelectItem key={m._id} value={m._id}>{m.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Cantidad de ciclos</Label>
                <Input
                  type="number"
                  min="1"
                  value={abortedForm.ciclos}
                  onChange={(e) => handleAbortedFormChange('ciclos', e.target.value)}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Fecha</Label>
                <Input
                  type="date"
                  value={abortedForm.fecha || getAbortedCycleDate()}
                  onChange={(e) => handleAbortedFormChange('fecha', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Razón</Label>
              <Select value={abortedForm.razon} onValueChange={(v) => handleAbortedFormChange('razon', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccionar razón..." />
                </SelectTrigger>
                <SelectContent>
                  {ABORT_REASONS.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {abortedForm.razon === 'Otro' && (
              <div>
                <Label className="text-sm">Especificar razón</Label>
                <Input
                  placeholder="Describe la razón..."
                  value={abortedForm.otraRazon}
                  onChange={(e) => handleAbortedFormChange('otraRazon', e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAbortedModalOpen(false); setAbortedForm(ABORTED_FORM_INIT); }}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleSaveAbortedCycle} disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Ban className="mr-2 h-4 w-4" />}
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default DryingCyclesPage;
